import { Box, TableCell } from "@mui/material";
import {
    DotLegalBreadCrumbModel,
    DotLegalButton,
    DotLegalOverflowMenu,
    DotLegalPageHeader,
    DotLegalTableWithControls,
    ITableHeader,
} from "@dotlegal/dotlegal-ui-components";
import CheckIcon from "@mui/icons-material/Check";
import TableRow from "@common/components/tableRow/TableRow";
import { useTranslation } from "@localization/useTranslation";
import { useApiKeys } from "./ApiKeys.hooks";
import { ApiKeyViewModel } from "./ApiKeys.types";
import CreateApiKeyDialog from "./createApiKeyDialog/CreateApiKeyDialog";
import ApiKeyCreatedDialog from "./apiKeyCreatedDialog/ApiKeyCreatedDialog";
import DeactivateApiKeyDialog from "./deactivateApiKeyDialog/DeactivateApiKeyDialog";
import TablePagePaper from "@common/components/tablePagePaper/TablePagePaper";

function ApiKeys() {
    const { translateString, translateDate } = useTranslation();
    const {
        apiKeys,
        isLoading,
        showCreateDialog,
        setShowCreateDialog,
        createdResult,
        setCreatedResult,
        apiKeyForDeactivation,
        closeDeactivateDialog,
        getMenuItems,
        onApiKeyCreated,
        onApiKeyDeactivated,
    } = useApiKeys();

    const breadCrumbs: Array<DotLegalBreadCrumbModel> = [{ name: translateString("apiKeys") }];

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <DotLegalPageHeader
                breadCrumbs={breadCrumbs}
                rightComponent={
                    <DotLegalButton buttonType="primary" size="medium" onClick={() => setShowCreateDialog(true)}>
                        {translateString("createApiKey")}
                    </DotLegalButton>
                }
            />

            <TablePagePaper>
                <DotLegalTableWithControls
                    internalScroll
                    hasOverflowMenu
                    headers={getHeaders(translateString)}
                    getUserSpecificPageLength={() => 30}
                    hideRowsPerPage
                    defaultOrderBy={"created"}
                    defaultOrder={"desc"}
                    clickableRows={false}
                    isLoading={isLoading}
                    labelRowsPerPage={translateString("showEntites")}
                    labelSearch={translateString("search")}
                    noOptionsLabel={translateString("noOptions")}
                    paginationLabelOf={translateString("labelOf")}
                    emptyText={translateString("noData")}
                    data={apiKeys ?? []}
                    renderRow={(row, i) => (
                        <TableRow key={i} inactive={!row.isActive} inactiveTooltip={translateString("deactivated")}>
                            <TableCell>{row.name}</TableCell>
                            <TableCell>{row.keyPrefix}</TableCell>
                            <TableCell>{translateDate(new Date(row.created))}</TableCell>
                            <TableCell>{row.lastUsed ? translateDate(new Date(row.lastUsed)) : "-"}</TableCell>
                            <TableCell>{row.isActive ? <CheckIcon fontSize="small" /> : ""}</TableCell>
                            <TableCell align={"right"}>
                                <DotLegalOverflowMenu items={getMenuItems(row)} />
                            </TableCell>
                        </TableRow>
                    )}
                />
            </TablePagePaper>

            {showCreateDialog && <CreateApiKeyDialog open={showCreateDialog} onCloseDialog={() => setShowCreateDialog(false)} onCreated={onApiKeyCreated} />}

            {createdResult && <ApiKeyCreatedDialog result={createdResult} onClose={() => setCreatedResult(undefined)} />}

            {apiKeyForDeactivation && (
                <DeactivateApiKeyDialog
                    id={apiKeyForDeactivation.id}
                    name={apiKeyForDeactivation.name}
                    onCloseDialog={closeDeactivateDialog}
                    onDeactivated={onApiKeyDeactivated}
                />
            )}
        </Box>
    );
}

function getHeaders(translateString: (key: string) => string) {
    const headers: Array<ITableHeader<ApiKeyViewModel>> = [];
    headers.push({ property: "name", text: translateString("name"), align: "left", showOnMobile: true });
    headers.push({ property: "keyPrefix", text: translateString("keyPrefix"), align: "left", showOnMobile: true });
    headers.push({ property: "created", text: translateString("created"), align: "left", showOnMobile: true });
    headers.push({ property: "lastUsed", text: translateString("lastUsed"), align: "left", showOnMobile: true });
    headers.push({ property: "isActive", text: translateString("active"), align: "left", showOnMobile: true });
    return headers;
}

export default ApiKeys;
